import React, { ReactNode } from "react";
import { useQuery } from "react-query";
import { Theme, ThemeProvider } from "@mui/material";
import { fetchAppInstance } from "services/appService";
import { createTheme, darken, lighten } from "@mui/material/styles";
import Loader from "shared/components/Loader";
import { getDecodedJwt } from "utils/auth";
import { IDecodedUser } from "modules/Auth/interfaces/authInterfaces";

interface IAppInstanceProps {
  children: ReactNode;
  theme: Theme;
}

function getFaviconEl() {
  return document.getElementById("favicon") as HTMLAnchorElement | null;
}

function getAppleTouchIcon() {
  return document.getElementById("appleTouchIcon") as HTMLAnchorElement | null;
}

function getappTitle() {
  return document.getElementById("appTitle");
}

const AppInstanceContext = React.createContext<{}>({});

const AppInstanceProvider = ({ children, theme }: IAppInstanceProps) => {
  const decodedUser: IDecodedUser = getDecodedJwt();

  const instanceSlug = process.env.REACT_APP_INSTANCE_SLUG || decodedUser?.instance_slug || "";

  const { data, isLoading } = useQuery(["appInstance", { instanceSlug }], fetchAppInstance, {
    enabled: !!instanceSlug,
    onSuccess: (data) => {
      const favicon: HTMLAnchorElement | null = getFaviconEl(); // Accessing favicon element
      const appTouch: HTMLAnchorElement | null = getAppleTouchIcon();
      const appTitle: HTMLElement | null = getappTitle();
      if (favicon) {
        favicon.href = data?.data?.logo;
      }
      if (appTouch) {
        appTouch.href = data?.data?.logo;
      }
      if (appTitle) {
        appTitle.innerHTML = `${data?.data?.name} EMS`;
      }
    },
  });

  const instanceTheme = React.useMemo(() => {
    if (data?.data?.color_variants) {
      return createTheme({
        ...theme,
        palette: {
          primary: {
            main: data?.data?.color_variants?.color_2,
            light: data?.data?.color_variants?.color_7,
            dark: data?.data?.color_variants?.color_8,
          },
          secondary: {
            main: data?.data?.color_variants?.color_2,
            light: data?.data?.color_variants?.color_8,
            dark: data?.data?.color_variants?.color_8,
          },
          // @ts-ignore
          tertiary: {
            main: data?.data?.color_variants?.color_1,
          },
          // @ts-ignore
          background: {
            ...theme.palette.background,
            // @ts-ignore
            avatar: data?.data?.color_variants?.color_2,
          },
        },
        components: {
          ...theme.components,
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 4,
                textTransform: "capitalize",
                color: data?.data?.color_variants?.color_8,
                "&:hover": {
                  // border: "none",
                  background: data?.data?.color_variants?.color_6,
                  color: data?.data?.color_variants?.color_2,
                },
              },
              outlinedPrimary: {
                border: "0px",
                color: data?.data?.color_variants?.color_8,
                background: data?.data?.color_variants?.color_1,
                "&:hover": {
                  border: "none",
                  background: data?.data?.color_variants?.color_7,
                  color: data?.data?.color_variants?.color_2,
                },
              },
              outlinedSecondary: {
                border: "0px",
                color: data?.data?.secondary_color,
                background: lighten(data?.data?.secondary_color, 0.9),
                "&:hover": {
                  border: "none",
                  background: lighten(data?.data?.secondary_color, 0.7),
                },
              },
            },
          },
        },
      });
    }
    return theme;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading) <Loader />;
  return (
    <AppInstanceContext.Provider value={{ ...data?.data }}>
      <ThemeProvider theme={instanceTheme}>{children}</ThemeProvider>
    </AppInstanceContext.Provider>
  );
};

export const useAppInstance: any = () => {
  return React.useContext(AppInstanceContext);
};

export default AppInstanceProvider;
